/*
 * Instant client-side filtering for the Journal (blog) article grid.
 *
 * The section renders every article in the current view up front: the cards for
 * the current pagination page are visible, and the remaining ("off-page") cards
 * are in the DOM but hidden. Filtering is therefore a pure visibility toggle --
 * no network, no markup generation.
 *
 * SECURITY: the query is attacker-controllable via ?q= in a crafted link, so it
 * is only ever written to the DOM through textContent or the input's value
 * property. innerHTML, RegExp built from input, and eval must never appear in
 * this file -- doing so would turn this into reflected DOM XSS on the shop
 * origin. See the matching note in main-blog.liquid.
 */

const MAX_QUERY_LENGTH = 100;
const DEBOUNCE_MS = 120;

class BlogSearch extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('[data-blog-search-form]');
    this.input = this.querySelector('[data-blog-search-input]');
    this.resetButton = this.querySelector('[data-blog-search-reset]');
    this.status = this.querySelector('[data-blog-search-status]');
    this.emptyState = this.querySelector('[data-blog-search-empty]');
    this.emptyText = this.querySelector('[data-blog-search-empty-text]');
    this.clearButton = this.querySelector('[data-blog-search-clear]');

    const scope = this.closest('.main-blog') || document;
    this.grid = scope.querySelector('[data-blog-search-grid]');
    this.pagination = scope.querySelector('.pagination-wrapper');
    this.cards = this.dedupe(Array.from(scope.querySelectorAll('[data-search]')));

    // Nothing to filter (or markup changed) -- stay out of the way entirely.
    if (!this.form || !this.input || !this.grid || this.cards.length === 0) return;

    this.collageClass = 'blog-articles--collage';
    this.hadCollage = this.grid.classList.contains(this.collageClass);
    this.debounceTimer = null;

    // Without JS the form falls back to Shopify's own search page; with JS we
    // filter in place instead.
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.applyFilter();
    });

    this.input.addEventListener('input', () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.applyFilter(), DEBOUNCE_MS);
    });

    // Native "search" input clear (the X in WebKit) fires a search event.
    this.input.addEventListener('search', () => this.applyFilter());

    if (this.resetButton) {
      this.resetButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.clear();
      });
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clear());
    }

    this.restoreFromUrl();
  }

  /*
   * The hidden off-page set is rendered unpaginated, so it repeats whatever is
   * already on the current page. Visible cards always come first in the DOM
   * (pinned, then this page, then the off-page block), so the first occurrence
   * of a URL is the one to keep and any later copy is the redundant one.
   */
  dedupe(cards) {
    const seen = new Set();

    return cards.filter((card) => {
      const link = card.querySelector('a[href]');
      const href = link ? link.getAttribute('href') : null;

      if (!href) return true;

      if (seen.has(href)) {
        card.remove();
        return false;
      }

      seen.add(href);
      return true;
    });
  }

  /* Read ?q= on load so a filtered view is linkable and survives back/forward. */
  restoreFromUrl() {
    const initial = new URLSearchParams(window.location.search).get('q');
    if (!initial) return;

    // Property assignment -- the value is never parsed as HTML.
    this.input.value = initial.slice(0, MAX_QUERY_LENGTH);
    this.applyFilter({ updateUrl: false });
  }

  get query() {
    return this.input.value.slice(0, MAX_QUERY_LENGTH).trim().toLowerCase();
  }

  applyFilter({ updateUrl = true } = {}) {
    const query = this.query;
    const tokens = query.split(/\s+/).filter(Boolean);

    if (tokens.length === 0) {
      this.reset();
      if (updateUrl) this.syncUrl('');
      return;
    }

    let matches = 0;

    this.cards.forEach((card) => {
      const haystack = card.getAttribute('data-search') || '';
      // Plain substring matching: no RegExp is ever built from user input.
      const isMatch = tokens.every((token) => haystack.includes(token));

      card.hidden = !isMatch;
      if (isMatch) matches += 1;
    });

    // :nth-child counts hidden elements, so the collage layout's full-width
    // rules would land on the wrong cards while filtering. Drop to a plain grid.
    this.grid.classList.remove(this.collageClass);
    if (this.pagination) this.pagination.hidden = true;
    this.toggleReset(true);

    this.setStatus(matches, query);
    this.toggleEmptyState(matches === 0, query);

    if (updateUrl) this.syncUrl(query);
  }

  /* Back to the server-rendered default: current page visible, rest hidden. */
  reset() {
    this.cards.forEach((card) => {
      card.hidden = card.hasAttribute('data-offpage');
    });

    if (this.hadCollage) this.grid.classList.add(this.collageClass);
    if (this.pagination) this.pagination.hidden = false;

    this.toggleReset(false);
    this.setStatus(null, '');
    this.toggleEmptyState(false, '');
  }

  clear() {
    this.input.value = '';
    this.reset();
    this.syncUrl('');
    this.input.focus();
  }

  toggleReset(show) {
    if (this.resetButton) this.resetButton.classList.toggle('hidden', !show);
  }

  setStatus(matches, query) {
    if (!this.status) return;

    if (matches === null) {
      this.status.textContent = '';
      return;
    }

    const noun = matches === 1 ? 'article' : 'articles';
    // textContent: the query is inert here even if it contains markup.
    this.status.textContent = `${matches} ${noun} matching “${query}”`;
  }

  toggleEmptyState(show, query) {
    if (!this.emptyState) return;

    this.emptyState.hidden = !show;
    if (show && this.emptyText) {
      this.emptyText.textContent = `No articles match “${query}”.`;
    }
  }

  /* Relative path + URLSearchParams: correctly encoded, same-origin by design. */
  syncUrl(query) {
    if (!window.history || !window.history.replaceState) return;

    const params = new URLSearchParams(window.location.search);

    if (query) {
      params.set('q', query);
    } else {
      params.delete('q');
    }

    const search = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
  }
}

if (!customElements.get('blog-search')) {
  customElements.define('blog-search', BlogSearch);
}
